import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Eyebrow } from './ui/Eyebrow';
import { MedalIcon } from './ui/MedalIcon';
import type { Achievement, UserAchievement } from '../types/social';

interface Props {
  friendId: string;
  friendName: string;
  catalog: Achievement[];
}

const RARITY_RANK: Record<string, number> = {
  legendary: 0,
  epic: 1,
  rare: 2,
  common: 3,
};

export function FriendMedalsRow({ friendId, friendName, catalog }: Props) {
  const [earned, setEarned] = useState<UserAchievement[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('user_achievements')
        .select('user_id, achievement_id, earned_at')
        .eq('user_id', friendId)
        .order('earned_at', { ascending: false });
      if (cancelled) return;
      if (error) {
        console.error('FriendMedalsRow load', error);
        setEarned([]);
        return;
      }
      setEarned((data ?? []) as UserAchievement[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [friendId]);

  if (earned === null) {
    return (
      <div>
        <Eyebrow color="#7A6B3F" className="mb-2">Medalhas</Eyebrow>
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-full bg-ink-100 animate-pulse" style={{ width: 40, height: 40 }} />
          ))}
        </div>
      </div>
    );
  }

  if (earned.length === 0) return null;

  // Sort: raridade asc + earned_at desc
  const earnedById = new Map(earned.map((e) => [e.achievement_id, e]));
  const top3 = earned
    .map((e) => catalog.find((a) => a.id === e.achievement_id))
    .filter((a): a is Achievement => Boolean(a))
    .sort((a, b) => {
      const ra = RARITY_RANK[a.rarity] ?? 99;
      const rb = RARITY_RANK[b.rarity] ?? 99;
      if (ra !== rb) return ra - rb;
      const ea = earnedById.get(a.id)?.earned_at ?? '';
      const eb = earnedById.get(b.id)?.earned_at ?? '';
      return eb < ea ? -1 : 1;
    })
    .slice(0, 3);

  if (top3.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Eyebrow color="#7A6B3F">Medalhas raras de {friendName.split(/\s+/)[0]}</Eyebrow>
        <RouterLink
          to={`/profile/${friendId}`}
          className="text-caption font-semibold text-green-700 hover:text-green-900 inline-flex items-center gap-1"
        >
          Ver perfil <ArrowRight size={12} />
        </RouterLink>
      </div>
      <div className="flex gap-3 items-center">
        {top3.map((m) => (
          <MedalIcon key={m.id} medal={m} size={44} />
        ))}
        {earned.length > 3 && (
          <span className="text-caption text-ink-500 font-semibold">+{earned.length - 3}</span>
        )}
      </div>
    </div>
  );
}
